import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Sparkles } from 'lucide-react';
import { StarRatingDisplay } from './StarRating';

type ReviewListItem = {
  id: string;
  rating: number;
  title: string | null;
  body: string | null;
  authorName: string;
  createdAt: number;
};

type ReviewListResponse = {
  data: ReviewListItem[];
  pagination: { limit: number; offset: number; hasMore: boolean; total: number };
  summary: { avgRating: number | null; reviewsCount: number };
};

// Public course-detail reviews surface. Renders avg + count near the top, then
// up to 10 most recent approved reviews. The list endpoint paginates but for
// MVP we don't expose a "load more" button — the first page covers the
// realistic moderated volume.
export function PublicCourseReviews({ slug, locale }: { slug: string; locale: 'sk' | 'cs' }) {
  const { t } = useTranslation();
  const query = useQuery<ReviewListResponse>({
    queryKey: ['public', 'course', slug, 'reviews'],
    queryFn: async () => {
      // The wrapper unwraps `data`; we want the full envelope to access summary
      // + pagination, so call fetch directly.
      const res = await fetch(`/api/v1/courses/${slug}/reviews?limit=10`, {
        credentials: 'same-origin',
      });
      if (!res.ok) throw new Error('reviews_fetch_failed');
      return (await res.json()) as ReviewListResponse;
    },
    enabled: Boolean(slug),
    retry: false,
  });

  if (query.isLoading) return null;
  if (query.isError || !query.data) return null;

  const { data: reviews, summary } = query.data;
  const dateFormatter = new Intl.DateTimeFormat(locale === 'cs' ? 'cs-CZ' : 'sk-SK', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });

  if (summary.reviewsCount === 0 || reviews.length === 0) {
    return (
      <div className="text-center">
        <p className="dr-eyebrow">
          {t('courses.reviews_eyebrow', { defaultValue: 'Hodnotenia' })}
        </p>
        <h2 className="mt-3 font-display text-h2 text-primary-dark">
          {t('courses.reviews_title')}
        </h2>
        <div className="mt-8 mx-auto inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/15 text-primary">
          <Sparkles size={22} />
        </div>
        <p className="mt-5 font-sans text-body text-text-secondary max-w-md mx-auto leading-relaxed">
          {t('courses.reviews_empty')}
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="text-center">
        <p className="dr-eyebrow">
          {t('courses.reviews_eyebrow', { defaultValue: 'Hodnotenia' })}
        </p>
        <h2 className="mt-3 font-display text-h2 text-primary-dark">
          {t('courses.reviews_title')}
        </h2>
        {summary.avgRating !== null && (
          <div className="mt-5 inline-flex items-center gap-3 rounded-pill bg-white/65 backdrop-blur-md border border-glass-border-strong px-5 py-2.5 shadow-glass-soft">
            <StarRatingDisplay value={summary.avgRating} size={20} />
            <span className="font-display text-h4 text-primary-dark leading-none">
              {summary.avgRating.toFixed(1)}
            </span>
            <span className="font-sans text-body-small text-text-secondary">
              {t('courses.reviews_count_label', { count: summary.reviewsCount })}
            </span>
          </div>
        )}
      </div>

      <ul className="mt-12 grid gap-4 md:gap-5 md:grid-cols-2 stagger-children">
        {reviews.map((r) => (
          <li key={r.id} className="dr-card-static p-5 sm:p-6 flex flex-col">
            <div className="flex items-center justify-between gap-3">
              <StarRatingDisplay value={r.rating} size={16} />
              <span className="font-sans text-caption text-text-muted">
                {dateFormatter.format(new Date(r.createdAt * 1000))}
              </span>
            </div>
            {r.title && (
              <h3 className="mt-3 font-display text-h4 text-primary-dark">{r.title}</h3>
            )}
            {r.body && (
              <p className="mt-2 font-sans text-body text-text-secondary leading-relaxed whitespace-pre-line">
                {r.body}
              </p>
            )}
            <p className="mt-auto pt-5 font-sans text-caption font-medium uppercase tracking-[0.14em] text-primary/80">
              — {r.authorName}
            </p>
          </li>
        ))}
      </ul>
    </div>
  );
}
